import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../../utils/api';

// ─── Fallback / demo data ─────────────────────────────────────────────────────

const FALLBACK_ITERATIONS = [
  {
    iteration_id: 'demo-iter-001', iteration_number: 1, iteration_name: 'Baseline Assessment',
    start_date: '2025-07-01', end_date: '2025-09-30', status: 'COMPLETED',
    overall_compliance_pct: 56.0, core_compliance_pct: 52.0, meets_all_thresholds: false,
    compliant_count: 340, total_assessed: 638,
  },
  {
    iteration_id: 'demo-iter-002', iteration_number: 2, iteration_name: 'Improvement Cycle 1',
    start_date: '2025-12-01', end_date: null, status: 'IN_PROGRESS',
    overall_compliance_pct: 75.3, core_compliance_pct: 78.0, meets_all_thresholds: true,
    compliant_count: 480, total_assessed: 638,
  },
];

const FALLBACK_CHAPTER_SCORES = [
  { chapter_code: 'AAC', chapter_name: 'Access, Assessment & Continuity',   compliance_percentage: 73.9, meets_minimum_threshold: true,  total_score: 613,  max_possible_score: 830,  compliant_count: 42, partial_count: 28, non_compliant_count: 15, na_count: 2 },
  { chapter_code: 'COP', chapter_name: 'Care of Patients',                   compliance_percentage: 81.0, meets_minimum_threshold: true,  total_score: 1012, max_possible_score: 1250, compliant_count: 68, partial_count: 42, non_compliant_count: 20, na_count: 5 },
  { chapter_code: 'MOM', chapter_name: 'Management of Medication',           compliance_percentage: 79.7, meets_minimum_threshold: true,  total_score: 510,  max_possible_score: 640,  compliant_count: 36, partial_count: 20, non_compliant_count: 10, na_count: 2 },
  { chapter_code: 'PRE', chapter_name: 'Patient Rights & Education',         compliance_percentage: 82.3, meets_minimum_threshold: true,  total_score: 395,  max_possible_score: 480,  compliant_count: 28, partial_count: 15, non_compliant_count:  7, na_count: 2 },
  { chapter_code: 'IPC', chapter_name: 'Infection Prevention & Control',     compliance_percentage: 83.4, meets_minimum_threshold: true,  total_score: 392,  max_possible_score: 470,  compliant_count: 30, partial_count: 12, non_compliant_count:  6, na_count: 1 },
  { chapter_code: 'PSQ', chapter_name: 'Patient Safety & Quality',           compliance_percentage: 85.2, meets_minimum_threshold: true,  total_score: 358,  max_possible_score: 420,  compliant_count: 24, partial_count: 14, non_compliant_count:  6, na_count: 2 },
  { chapter_code: 'ROM', chapter_name: 'Responsibility of Management',       compliance_percentage: 86.4, meets_minimum_threshold: true,  total_score: 285,  max_possible_score: 330,  compliant_count: 18, partial_count: 12, non_compliant_count:  5, na_count: 2 },
  { chapter_code: 'FMS', chapter_name: 'Facility Management & Safety',       compliance_percentage: 84.6, meets_minimum_threshold: true,  total_score: 330,  max_possible_score: 390,  compliant_count: 22, partial_count: 13, non_compliant_count:  6, na_count: 2 },
  { chapter_code: 'HRM', chapter_name: 'Human Resource Management',          compliance_percentage: 79.2, meets_minimum_threshold: true,  total_score: 570,  max_possible_score: 720,  compliant_count: 38, partial_count: 24, non_compliant_count: 12, na_count: 2 },
  { chapter_code: 'IMS', chapter_name: 'Information Management System',      compliance_percentage: 82.6, meets_minimum_threshold: true,  total_score: 347,  max_possible_score: 420,  compliant_count: 24, partial_count: 13, non_compliant_count:  6, na_count: 1 },
];

// ─── State interface ───────────────────────────────────────────────────────────

interface ComplianceState {
  iterations: any[];
  currentIteration: any | null;
  chapterScores: any[];
  // FIX: track which iteration_id the current chapterScores belong to so
  // stale data is never shown for a different iteration.
  chapterScoresIterationId: string | null;
  accreditationReadiness: { ready: boolean; overallPct: number; failingChapters: any[] } | null;
  isLoading: boolean;
  isLoadingScores: boolean;   // separate flag so the scores spinner doesn't block the whole page
  error: string | null;
  lastSaved: string | null;
}

const initialState: ComplianceState = {
  iterations: [],
  currentIteration: null,
  chapterScores: [],
  chapterScoresIterationId: null,
  accreditationReadiness: null,
  isLoading: false,
  isLoadingScores: false,
  error: null,
  lastSaved: null,
};

// ─── Thunks ────────────────────────────────────────────────────────────────────

export const fetchIterations = createAsyncThunk(
  'compliance/fetchIterations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/compliance/iterations');
      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || 'Cannot connect to server. Showing demo data.',
      );
    }
  },
);

export const createIteration = createAsyncThunk(
  'compliance/createIteration',
  async (name: string, { rejectWithValue }) => {
    try {
      const response = await api.post(
        '/compliance/iterations',
        { iteration_name: name },
        { validateStatus: (s) => s >= 200 && s < 300 },
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data?.error || 'Failed to create iteration.',
      );
    }
  },
);

export const deleteIteration = createAsyncThunk(
  'compliance/deleteIteration',
  async (iterationId: string, { rejectWithValue }) => {
    try {
      await api.delete(`/compliance/iterations/${iterationId}`);
      return iterationId;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.response?.data?.error || 'Failed to delete iteration.',
      );
    }
  },
);

// ── FIX: fetchChapterScores now carries the iterationId in its meta so the
//   fulfilled reducer can verify the payload belongs to the most-recently-
//   requested iteration and not a stale, out-of-order response.
export const fetchChapterScores = createAsyncThunk(
  'compliance/fetchChapterScores',
  async (iterationId: string, { rejectWithValue }) => {
    try {
      const response = await api.get('/compliance/scores/chapter', {
        params: { iteration_id: iterationId },
      });
      // Return both the scores payload AND the iterationId so the reducer
      // can stamp chapterScoresIterationId correctly.
      return { iterationId, ...response.data };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message || 'Failed to load scores');
    }
  },
);

export const updateAssessment = createAsyncThunk(
  'compliance/updateAssessment',
  async ({ assessmentId, data }: { assessmentId: string; data: any }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/compliance/assessments/${assessmentId}`, data);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || { message: 'Failed to save assessment' });
    }
  },
);

// ─── Slice ─────────────────────────────────────────────────────────────────────

const complianceSlice = createSlice({
  name: 'compliance',
  initialState,
  reducers: {
    setCurrentIteration: (state, action) => {
      state.currentIteration = action.payload;
      // When the current iteration is cleared, reset scores so stale data
      // from a previous selection doesn't bleed through.
      if (action.payload === null) {
        state.chapterScores = [];
        state.chapterScoresIterationId = null;
      }
    },
    clearComplianceError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── fetchIterations ────────────────────────────────────────────────────
      .addCase(fetchIterations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchIterations.fulfilled, (state, action) => {
        // Always use the real payload from the server — never fall back to demo data.
        // An empty array means the user has no iterations, which is a valid state.
        state.iterations = Array.isArray(action.payload) ? action.payload : [];
        // If there are no iterations, clear stale accreditationReadiness so the
        // "Latest Compliance" card shows '--' instead of old data.
        if (state.iterations.length === 0) {
          state.accreditationReadiness = null;
        }
        state.isLoading = false;
      })
      .addCase(fetchIterations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        // On network error keep whatever iterations are already in state (do not wipe them),
        // but do NOT inject fake demo data.
      })

      // ── createIteration ────────────────────────────────────────────────────
      .addCase(createIteration.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createIteration.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (action.payload?.data) {
          state.iterations.unshift(action.payload.data);
          state.currentIteration = action.payload.data;
          state.lastSaved = new Date().toISOString();
        }
      })
      .addCase(createIteration.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // ── deleteIteration ────────────────────────────────────────────────────
      .addCase(deleteIteration.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteIteration.fulfilled, (state, action) => {
        state.isLoading = false;
        state.iterations = state.iterations.filter(
          (it) => it.iteration_id !== action.payload,
        );
        if (state.currentIteration?.iteration_id === action.payload) {
          state.currentIteration = null;
          state.chapterScores = [];
          state.chapterScoresIterationId = null;
        }
        // Clear stale readiness data when the last iteration is deleted
        if (state.iterations.length === 0) {
          state.accreditationReadiness = null;
          state.chapterScores = [];
          state.chapterScoresIterationId = null;
        }
      })
      .addCase(deleteIteration.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // ── fetchChapterScores ─────────────────────────────────────────────────
      // FIX: use isLoadingScores so the main page layout doesn't re-render the
      // whole loading skeleton every time the user switches iteration in
      // SelfAssessment; only the score table area shows a spinner.
      .addCase(fetchChapterScores.pending, (state) => {
        state.isLoadingScores = true;
        state.isLoading = true;   // keep isLoading true so SelfAssessment can also check it
      })
      .addCase(fetchChapterScores.fulfilled, (state, action) => {
        state.isLoadingScores = false;
        state.isLoading = false;

        // FIX: stamp which iteration these scores belong to
        state.chapterScoresIterationId = action.payload.iterationId ?? null;

        // Use real server data; empty array is valid (no assessments yet)
        state.chapterScores = Array.isArray(action.payload?.data) ? action.payload.data : [];
        if (action.payload?.accreditationReadiness !== undefined) {
          state.accreditationReadiness = action.payload.accreditationReadiness;
        }
      })
      .addCase(fetchChapterScores.rejected, (state, action) => {
        state.isLoadingScores = false;
        state.isLoading = false;
        state.error = action.payload as string;
        // Keep existing chapterScores on error — do not inject demo data
      })

      // ── updateAssessment ───────────────────────────────────────────────────
      .addCase(updateAssessment.fulfilled, (state) => {
        state.error = null;
        state.lastSaved = new Date().toISOString();
      })
      .addCase(updateAssessment.rejected, (state, action) => {
        state.error = (action.payload as any)?.message || 'Failed to save';
      });
  },
});

export const { setCurrentIteration, clearComplianceError } = complianceSlice.actions;
export default complianceSlice.reducer;